import { createLink, type LinkComponent } from '@tanstack/react-router';
import { forwardRef, type AnchorHTMLAttributes, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cx, merge } from '../lib/cx';

export type ButtonVariant = 'primary' | 'secondary' | 'quiet' | 'coral' | 'dark' | 'ghost';
export type ButtonSize = 'xl' | 'lg' | 'md' | 'sm';

const VARIANT: Record<ButtonVariant, string> = {
  // teal filled: the one big action on a card
  primary: 'bg-teal text-deep font-bold hover:brightness-[0.96] active:brightness-[0.92]',
  // deep blue outline
  secondary: 'bg-white text-deep font-semibold border-[1.5px] border-deep hover:bg-sand',
  // sand outline, the quietest choice
  quiet: 'bg-white text-deep font-semibold border-[1.5px] border-sand-dark hover:bg-sand',
  coral: 'bg-coral text-deep font-bold hover:brightness-[0.97] active:brightness-[0.93]',
  dark: 'bg-deep text-white font-semibold hover:bg-deep-light',
  ghost: 'bg-white text-deep font-semibold border border-sand-dark hover:bg-sand',
};

const SIZE: Record<ButtonSize, string> = {
  // Titan density: 36px buttons with 13px text, 40px for the one big action on a card.
  xl: 'h-10 px-5 text-strong rounded-[20px] gap-2',
  lg: 'h-10 px-5 text-strong rounded-[20px] gap-2',
  md: 'h-9 px-4 text-body rounded-[18px] gap-2',
  sm: 'h-8 px-3.5 text-body rounded-[16px] gap-1.5',
};

export function buttonClass(variant: ButtonVariant = 'secondary', size: ButtonSize = 'md', className?: string) {
  const base = cx(
    'inline-flex shrink-0 items-center justify-center whitespace-nowrap text-center transition-[background-color,filter,box-shadow,transform] duration-150 select-none',
    'disabled:cursor-not-allowed disabled:opacity-55',
    VARIANT[variant],
    SIZE[size],
  );
  return merge(base, className);
}

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: ReactNode;
  busy?: boolean;
};

/** Big friendly buttons: teal primary, deep-blue outline secondary, sand outline quiet. */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'secondary', size = 'md', icon, busy = false, className, children, type = 'button', disabled, ...rest },
  ref,
) {
  return (
    <button ref={ref} type={type} className={buttonClass(variant, size, className)} disabled={disabled || busy} aria-busy={busy || undefined} {...rest}>
      {icon}
      {children}
    </button>
  );
});

type AnchorProps = AnchorHTMLAttributes<HTMLAnchorElement> & { variant?: ButtonVariant; size?: ButtonSize; icon?: ReactNode };

const ButtonAnchor = forwardRef<HTMLAnchorElement, AnchorProps>(function ButtonAnchor(
  { variant = 'secondary', size = 'md', icon, className, children, ...rest },
  ref,
) {
  return (
    <a ref={ref} className={buttonClass(variant, size, className)} {...rest}>
      {icon}
      {children}
    </a>
  );
});

const CreatedButtonLink = createLink(ButtonAnchor);

/** A router link that looks like a button. */
export const ButtonLink: LinkComponent<typeof ButtonAnchor> = (props) => <CreatedButtonLink preload="intent" {...props} />;
